import { doorGroup, type DoorLook, type DoorStyle } from './door';
import { esc, idSource, n, safeHex, shade } from './svg';

/**
 * A cabinet drawn front-on with a projected side and top, for product cards and
 * the cabinet builder. Ported from the site mockup (`cabinetSVG`), geometry kept.
 */
export type CabinetType = 'base' | 'wall' | 'tall' | 'drawers' | 'sink';

export interface CabinetSvgOptions {
  type?: CabinetType;
  /** Carcass width, height and depth in mm. */
  w: number;
  h: number;
  d?: number;
  style?: DoorStyle;
  /** Front colour. */
  colour?: string;
  wood?: boolean;
  /** Carcass board colour. */
  carcass?: string;
  /** Number of doors; defaults to 2 above 600 mm wide, else 1. */
  doors?: number;
  /** The door range's own look. */
  look?: DoorLook;
  /** A tall unit's upper door height in mm: a row of upper doors above the lower ones. */
  upper?: number;
  /** Number of drawers, for a drawer unit. */
  drawers?: number;
  /** Draw the wall unit's doors glazed. */
  glazed?: boolean;
  /** No fronts: show the shelves inside. */
  open?: boolean;
  shelves?: number;
  handle?: 'bar' | 'knob' | 'cup' | 'none';
  label?: string;
  idPrefix?: string;
}

export function cabinetSvg(o: CabinetSvgOptions): string {
  const ids = idSource(o.idPrefix ?? 'cab');
  const w = o.w;
  const h = o.h;
  const d = o.d ?? 560;
  const type = o.type ?? 'base';
  const style: DoorStyle = o.style ?? 'shaker';
  const hex = safeHex(o.colour);
  const carcass = safeHex(o.carcass, '#f1ede6');
  const k = 0.32; // side projection
  const sx = d * k * 0.9;
  const sy = d * k * 0.45;
  const plinth = type === 'base' || type === 'drawers' || type === 'sink' || type === 'tall' ? 100 : 0;
  const bodyH = h;
  const vw = w + sx + 20;
  const vh = bodyH + plinth + sy + 20;
  let s = `<svg viewBox="-10 ${n(-sy - 10)} ${n(vw)} ${n(vh)}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${esc(o.label || 'cabinet')}">`;
  s += `<polygon points="${n(w)},0 ${n(w + sx)},${n(-sy)} ${n(w + sx)},${n(bodyH - sy)} ${n(w)},${n(bodyH)}" fill="${shade(carcass, -0.12)}"/>`;
  s += `<polygon points="0,0 ${n(sx)},${n(-sy)} ${n(w + sx)},${n(-sy)} ${n(w)},0" fill="${shade(carcass, 0.4)}"/>`;
  s += `<rect width="${n(w)}" height="${n(bodyH)}" fill="${carcass}" stroke="${shade(carcass, -0.25)}" stroke-width="2"/>`;
  if (plinth) {
    s += `<rect x="30" y="${n(bodyH)}" width="${n(w - 30)}" height="${n(plinth)}" fill="${shade(hex, -0.25)}"/>`;
    s += `<polygon points="${n(w)},${n(bodyH)} ${n(w + sx * 0.5)},${n(bodyH - sy * 0.5)} ${n(w + sx * 0.5)},${n(bodyH + plinth - sy * 0.5)} ${n(w)},${n(bodyH + plinth)}" fill="${shade(hex, -0.4)}"/>`;
  }
  const gap = 3;
  const e = 2;
  const wood = Boolean(o.wood);
  if (o.open) {
    s += `<rect x="18" y="18" width="${n(w - 36)}" height="${n(bodyH - 36)}" fill="${shade(carcass, -0.06)}"/>`;
    const count = o.shelves || 1;
    for (let i = 1; i <= count; i++) {
      const yy = 18 + ((bodyH - 36) / (count + 1)) * i;
      s += `<rect x="18" y="${n(yy)}" width="${n(w - 36)}" height="18" fill="${shade(carcass, 0.2)}" stroke="${shade(carcass, -0.2)}"/>`;
    }
  } else if (type === 'drawers') {
    const count = o.drawers || 3;
    const shares = count === 3 ? [0.22, 0.36, 0.42] : count === 2 ? [0.5, 0.5] : Array(count).fill(1 / count);
    let yy = e;
    for (let i = 0; i < count; i++) {
      const dh = (bodyH - 2 * e - gap * (count - 1)) * (shares[i] as number);
      s += doorGroup(ids, e, yy, w - 2 * e, dh, style === 'glazed' ? 'shaker' : style, hex, {
        look: o.look,
        wood,
        handle: o.handle === 'none' || style === 'jpull' ? 'none' : 'cup',
      });
      yy += dh + gap;
    }
  } else {
    const doors = o.doors || (w > 600 ? 2 : 1);
    const dw = (w - 2 * e - gap * (doors - 1)) / doors;
    // Stacked (a tall unit's upper and lower doors) or one full-height row.
    const upper = o.upper && o.upper > 0 && o.upper < bodyH - 2 * e - gap ? o.upper : 0;
    const rows = upper
      ? [
          { y: e, h: upper, at: 'bottom' as const },
          { y: e + upper + gap, h: bodyH - 2 * e - upper - gap, at: 'top' as const },
        ]
      : [{ y: e, h: bodyH - 2 * e, at: type === 'wall' ? ('bottom' as const) : type === 'tall' ? ('centre' as const) : ('top' as const) }];
    for (const row of rows) {
      for (let i = 0; i < doors; i++) {
        const hinge = doors === 2 ? (i === 0 ? 'left' : 'right') : 'left';
        s += doorGroup(ids, e + i * (dw + gap), row.y, dw, row.h, type === 'wall' && o.glazed ? 'glazed' : style, hex, {
          look: o.look,
          wood,
          hinge,
          handle: style === 'jpull' ? 'none' : o.handle || 'bar',
          handleAt: row.at,
        });
      }
    }
  }
  return `${s}</svg>`;
}
